/**
 * Capacity Engine — crew-day inventory model for scheduling at scale.
 * ─────────────────────────────────────────────────────────────────────────
 * At $1B operator scale, the constrained resource is **install crew-days**,
 * not appointments. This engine treats the planning surface as a grid of
 * crew rows × day columns, where each cell is one bookable slot annotated
 * with:
 *   • the job assigned (if any)
 *   • a fit score (0-100) — derived from existing dispatchRecommendation
 *   • a readiness gate — derived from existing readiness engine
 *   • a conflict flag — derived from existing useSchedulingConflicts
 *
 * ADDITIVE — composes existing engines, mutates nothing, owns no status.
 * Pure data layer: no React, no UI imports.
 */
import {
  startOfWeek, endOfWeek, addDays, format, parseISO, isValid, isSameDay,
} from "date-fns";
import type { Job, Issue, Contractor, ContractorOpportunity, CustomerCredit } from "@/data/types";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import type { MaterialKit } from "@/data/materialKit";
import { getDispatchRecommendations } from "@/data/dispatchRecommendation";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";
import {
  advanceInstallCalendarDay,
  clampInstallShifts,
  getInstallCalendarShiftCountFromRange,
} from "@/data/scheduling/installSpan";

export const UNASSIGNED_ROW_KEY = "__unassigned__";

// ── Types ──────────────────────────────────────────────────────────────

export type CellState =
  | "open"            // crew-day available, no job
  | "tentative"       // job assigned but not readiness-cleared
  | "ready"           // job assigned and ready for install
  | "in_progress"     // install actively running
  | "completed"       // install completed
  | "conflict";       // multiple jobs assigned same day to same crew

export interface CapacityCell {
  crewName: string;
  dateIso: string;          // YYYY-MM-DD
  state: CellState;
  job: Job | null;
  fitScore: number | null;  // 0-100 if job present, else null
  readyForInstall: boolean;
  blockerLabel: string | null;
  conflictCount: number;    // 0 = clear, ≥2 = conflict
  /** 1-based shift position within the job's span (1 of N). null when empty. */
  shiftIndex: number | null;
  /** Total shifts the job consumes across the week. null when empty. */
  totalShifts: number | null;
  /** True for cells that aren't the first day of a multi-day job (continuation). */
  isContinuation: boolean;
}

export interface CapacityRow {
  crewName: string;
  crewType: "Internal Crew" | "Subcontractor" | "Unassigned";
  /** This week's booked install-days. */
  bookedDays: number;
  /** Operator-tunable max (default 5/wk). */
  capacityDays: number;
  utilizationPct: number;
  cells: CapacityCell[];     // length = days in window
}

export interface CapacityGrid {
  weekStart: string;         // YYYY-MM-DD
  weekEnd: string;           // YYYY-MM-DD
  days: { iso: string; label: string; isToday: boolean }[];
  rows: CapacityRow[];
  /** Jobs sold but neither scheduled nor assigned — the queue feeding the grid. */
  unscheduled: UnscheduledJob[];
  /**
   * Per-job crew fit map: jobId → (crewName → fitScore 0-100).
   * Already computed during readiness pass — surfaced here so consumers
   * (e.g. Better Crew suggestions) can read sibling-crew fit without
   * recomputing dispatch recommendations per render.
   */
  jobFitMap: Map<string, Map<string, number>>;
}

export interface UnscheduledJob {
  job: Job;
  /** Composite "ready to schedule?" — derived from readiness + payment gates. */
  readyForInstall: boolean;
  /** Top blocker label if not ready (e.g. "Materials missing"). */
  topBlocker: string | null;
  /** Days since sold (proxy for urgency). */
  daysSinceSold: number;
}

// ── Defaults ──────────────────────────────────────────────────────────

/** Default crew capacity = 5 install-days per week (M-F). Operator-tunable later. */
const DEFAULT_CAPACITY_DAYS_PER_WEEK = 5;

// ── Builder ───────────────────────────────────────────────────────────

interface BuildOpts {
  jobs: Job[];
  issues: Issue[];
  contractors: Contractor[];
  contractorOpportunities: ContractorOpportunity[];
  credits: CustomerCredit[];
  buildReviewEvents?: BuildReviewEvent[];
  materialKits?: MaterialKit[];
  referenceDate?: Date;
  /** Override per-crew weekly capacity. */
  capacityDaysPerWeek?: number;
}

export function buildCapacityGrid(opts: BuildOpts): CapacityGrid {
  const {
    jobs, issues, contractors, contractorOpportunities, credits,
    buildReviewEvents = [], materialKits = [], referenceDate = new Date(),
    capacityDaysPerWeek = DEFAULT_CAPACITY_DAYS_PER_WEEK,
  } = opts;

  const wStart = startOfWeek(referenceDate, { weekStartsOn: 1 });
  const wEnd = endOfWeek(referenceDate, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(wStart, i);
    return {
      iso: format(d, "yyyy-MM-dd"),
      label: format(d, "EEE d"),
      isToday: isSameDay(d, referenceDate),
    };
  });

  // Collect every known crew: those currently assigned to a job + active subs/internal crews.
  const crewMap = new Map<string, "Internal Crew" | "Subcontractor">();
  for (const j of jobs) {
    if (j.assignmentType === "Internal Crew" && j.assignedCrew) {
      crewMap.set(j.assignedCrew, "Internal Crew");
    } else if (j.assignmentType === "Subcontractor" && j.assignedSubcontractor) {
      crewMap.set(j.assignedSubcontractor, "Subcontractor");
    }
  }
  // Add active contractors (subs) even if not currently assigned — they're capacity.
  for (const c of contractors) {
    if (c.active) {
      if (!crewMap.has(c.companyName)) crewMap.set(c.companyName, "Subcontractor");
    }
  }

  // Per-job readiness/blocker (compute once, reuse across cells).
  // IMPORTANT: "ready on the board" (scheduled + dispatch-cleared) is NOT the
  // same as "ready to book" (unscheduled + safe to place). The rail needs the
  // latter, otherwise an unscheduled job can never qualify because schedule and
  // crew are missing by definition.
  const jobReadiness = new Map<string, {
    installReady: boolean;
    scheduleReady: boolean;
    blocker: string | null;
    fitToCrew: Map<string, number>;
  }>();
  for (const j of jobs) {
    if (j.currentStage === "Completed") continue;
    const externallyManagedBypass = isExternalManagedImport(j);
    const rec = getDispatchRecommendations(
      j,
      contractors,
      contractorOpportunities,
      jobs,
      issues,
      credits,
      buildReviewEvents,
      materialKits,
    );
    const preSpendGate = derivePreSpendGate(j);
    const fitMap = new Map<string, number>();
    for (const r of rec.recommendations) {
      fitMap.set(r.contractor.companyName, r.score);
    }
    const installReady = externallyManagedBypass || (rec.jobReady && rec.gateBlockers.length === 0);
    const scheduleReady = externallyManagedBypass || (preSpendGate.cleared && rec.gateBlockers.length === 0);
    const blocker = externallyManagedBypass
      ? null
      : !preSpendGate.cleared
        ? preSpendGate.shortReason
        : rec.gateBlockers[0]?.label ?? null;
    jobReadiness.set(j.id, { installReady, scheduleReady, blocker, fitToCrew: fitMap });
  }

  // Build rows
  const rows: CapacityRow[] = [];
  for (const [crewName, crewType] of crewMap) {
    // Index cells: { dateIso → { job, shiftIndex, totalShifts }[] }
    // Spanning is computed once per job: it occupies shiftIndex 1…N over
    // contiguous calendar days starting from scheduledInstallDate. Booking
    // suggestions may prefer business days, but once an install is booked the
    // plan grid should visibly consume every calendar block in that span.
    type DayEntry = { job: Job; shiftIndex: number; totalShifts: number };
    const dayJobMap = new Map<string, DayEntry[]>();
    for (const j of jobs) {
      const isThisCrew =
        (j.assignmentType === "Internal Crew" && j.assignedCrew === crewName) ||
        (j.assignmentType === "Subcontractor" && j.assignedSubcontractor === crewName);
      if (!isThisCrew) continue;
      if (!j.scheduledInstallDate) continue;
      const parsed = parseISO(j.scheduledInstallDate);
      if (!isValid(parsed)) continue;
      const explicitShifts = clampInstallShifts(j.scheduledShifts);
      const rangeShifts = getInstallCalendarShiftCountFromRange(j.scheduledStartTime, j.scheduledEndTime);
      const totalShifts = Math.max(explicitShifts, rangeShifts);
      let cursor = parsed;
      for (let i = 0; i < totalShifts; i++) {
        if (cursor >= wStart && cursor <= wEnd) {
          const iso = format(cursor, "yyyy-MM-dd");
          const arr = dayJobMap.get(iso) || [];
          arr.push({ job: j, shiftIndex: i + 1, totalShifts });
          dayJobMap.set(iso, arr);
        }
        cursor = advanceInstallCalendarDay(cursor);
      }
    }

    const cells: CapacityCell[] = days.map((d) => {
      const dayEntries = dayJobMap.get(d.iso) || [];
      if (dayEntries.length === 0) {
        return {
          crewName, dateIso: d.iso, state: "open" as const,
          job: null, fitScore: null, readyForInstall: false,
          blockerLabel: null, conflictCount: 0,
          shiftIndex: null, totalShifts: null, isContinuation: false,
        };
      }
      const { job, shiftIndex, totalShifts } = dayEntries[0];
      const r = jobReadiness.get(job.id);
      const fit = r?.fitToCrew.get(crewName) ?? null;
      const conflictCount = dayEntries.length;
      let state: CellState;
      if (conflictCount > 1) {
        state = "conflict";
      } else if (job.currentStage === "Completed") {
        state = "completed";
      } else if (job.currentStage === "In Progress") {
        state = "in_progress";
      } else if (r?.installReady) {
        state = "ready";
      } else {
        state = "tentative";
      }
      return {
        crewName, dateIso: d.iso, state,
        job, fitScore: fit,
        readyForInstall: r?.installReady ?? false,
        blockerLabel: r?.blocker ?? null,
        conflictCount,
        shiftIndex, totalShifts,
        isContinuation: shiftIndex > 1,
      };
    });

    const bookedDays = cells.filter(c => c.state !== "open").length;
    const utilizationPct = Math.min(100, Math.round((bookedDays / capacityDaysPerWeek) * 100));

    rows.push({
      crewName, crewType,
      bookedDays, capacityDays: capacityDaysPerWeek,
      utilizationPct, cells,
    });
  }

  // Stable row order: alphabetical by crew name. Operators build muscle
  // memory ("Crew A is row 3") — resorting by bookedDays after every move
  // shuffled the grid under their cursor on every drop. The week's row set
  // only changes when the week changes, not when a single booking moves.
  // (Selection of WHICH crews appear when there are too many still uses
  //  load-aware logic below; only the *display* order is stable.)
  rows.sort((a, b) => a.crewName.localeCompare(b.crewName));

  // ── Unassigned row — scheduled jobs with no crew ──────────────────────
  // Built after the sort so it always pins first regardless of alphabetical order.
  {
    type DayEntry = { job: Job; shiftIndex: number; totalShifts: number };
    const dayJobMap = new Map<string, DayEntry[]>();
    for (const j of jobs) {
      const hasDate = Boolean(j.scheduledInstallDate?.trim());
      const hasCrew = Boolean(j.assignedCrew?.trim()) || Boolean(j.assignedSubcontractor?.trim());
      if (!hasDate || hasCrew) continue;
      const parsed = parseISO(j.scheduledInstallDate!);
      if (!isValid(parsed)) continue;
      const explicitShifts = clampInstallShifts(j.scheduledShifts);
      const rangeShifts = getInstallCalendarShiftCountFromRange(j.scheduledStartTime, j.scheduledEndTime);
      const totalShifts = Math.max(explicitShifts, rangeShifts);
      let cursor = parsed;
      for (let i = 0; i < totalShifts; i++) {
        if (cursor >= wStart && cursor <= wEnd) {
          const iso = format(cursor, "yyyy-MM-dd");
          const arr = dayJobMap.get(iso) || [];
          arr.push({ job: j, shiftIndex: i + 1, totalShifts });
          dayJobMap.set(iso, arr);
        }
        cursor = advanceInstallCalendarDay(cursor);
      }
    }
    const unassignedCells: CapacityCell[] = days.map((d) => {
      const entries = dayJobMap.get(d.iso) || [];
      if (entries.length === 0) {
        return {
          crewName: UNASSIGNED_ROW_KEY, dateIso: d.iso, state: "open" as const,
          job: null, fitScore: null, readyForInstall: false,
          blockerLabel: null, conflictCount: 0,
          shiftIndex: null, totalShifts: null, isContinuation: false,
        };
      }
      const { job, shiftIndex, totalShifts } = entries[0];
      return {
        crewName: UNASSIGNED_ROW_KEY, dateIso: d.iso,
        state: "tentative" as const,
        job, fitScore: null, readyForInstall: false,
        blockerLabel: null, conflictCount: entries.length,
        shiftIndex, totalShifts,
        isContinuation: shiftIndex > 1,
      };
    });
    rows.unshift({
      crewName: UNASSIGNED_ROW_KEY,
      crewType: "Unassigned",
      bookedDays: 0,
      capacityDays: 0,
      utilizationPct: 0,
      cells: unassignedCells,
    });
  }

  // Cap visible rows at TARGET_ROWS so the grid breathes and stays scannable.
  // Selection strategy ensures the operator always has *both* signal:
  //   • busy crews (where redistribution decisions live)
  //   • crews with open capacity (where work can land)
  // Without this, a board where every top crew is saturated leaves zero
  // droppable cells and breaks the cell-to-cell rescheduling flow.
  const TARGET_ROWS = 8;
  const MIN_OPEN_ROWS = 4;
  if (rows.length > TARGET_ROWS) {
    const byLoad = [...rows].sort((a, b) => b.bookedDays - a.bookedDays);
    const withOpen = byLoad.filter(r => r.bookedDays < r.capacityDays);
    const fullyBooked = byLoad.filter(r => r.bookedDays >= r.capacityDays);
    const openSlots = Math.max(MIN_OPEN_ROWS, TARGET_ROWS - fullyBooked.length);
    const keptOpen = withOpen.slice(0, openSlots);
    const keptFull = fullyBooked.slice(0, TARGET_ROWS - keptOpen.length);
    const kept = new Set([...keptFull, ...keptOpen].map(r => r.crewName));
    // Preserve the stable alphabetical order for the kept set.
    const merged = rows.filter(r => kept.has(r.crewName));
    rows.length = 0;
    rows.push(...merged);
  }

  // Hard cap — guarantee never more than TARGET_ROWS.
  if (rows.length > TARGET_ROWS) rows.length = TARGET_ROWS;

  // Unscheduled queue: sold/active jobs without scheduledInstallDate.
  const unscheduled: UnscheduledJob[] = jobs
    .filter(j =>
      j.currentStage !== "Completed" &&
      !j.scheduledInstallDate
    )
    .map(j => {
      const r = jobReadiness.get(j.id);
      const sold = j.activityHistory?.[0]?.date ? new Date(j.activityHistory[0].date) : referenceDate;
      const daysSinceSold = Math.max(0, Math.floor((referenceDate.getTime() - sold.getTime()) / (1000 * 60 * 60 * 24)));
      return {
        job: j,
        // Historical field name kept for compatibility; semantics here are
        // "ready to book onto the board" rather than "already ready to install".
        readyForInstall: r?.scheduleReady ?? false,
        topBlocker: r?.blocker ?? null,
        daysSinceSold,
      };
    })
    // Ready-to-schedule first, then by age (oldest = most urgent).
    .sort((a, b) => {
      if (a.readyForInstall !== b.readyForInstall) return a.readyForInstall ? -1 : 1;
      return b.daysSinceSold - a.daysSinceSold;
    });

  // Surface the already-computed per-job fit map. We rebuild it here as a
  // plain Map<jobId, Map<crewName, score>> from the readiness pass — no new
  // dispatch-recommendation work is performed.
  const jobFitMap = new Map<string, Map<string, number>>();
  for (const [jobId, r] of jobReadiness) {
    jobFitMap.set(jobId, r.fitToCrew);
  }

  return {
    weekStart: format(wStart, "yyyy-MM-dd"),
    weekEnd: format(wEnd, "yyyy-MM-dd"),
    days,
    rows,
    unscheduled,
    jobFitMap,
  };
}

/**
 * Recommend the best crew/day pair for a single unscheduled job. Used by the
 * "Auto-place" affordance on each unscheduled card. Returns null if no cell
 * is open in the visible week.
 */
export function recommendPlacement(
  grid: CapacityGrid,
  jobId: string,
  jobs: Job[],
  contractors: Contractor[],
  contractorOpportunities: ContractorOpportunity[],
  issues: Issue[],
  credits: CustomerCredit[],
  buildReviewEvents: BuildReviewEvent[] = [],
): { crewName: string; dateIso: string; fitScore: number } | null {
  const job = jobs.find(j => j.id === jobId);
  if (!job) return null;
  const rec = getDispatchRecommendations(
    job, contractors, contractorOpportunities, jobs, issues, credits, buildReviewEvents,
    materialKits,
  );
  // Walk recommendations top-down; pick first crew with an open day this week.
  for (const r of rec.recommendations) {
    const row = grid.rows.find(rw => rw.crewName === r.contractor.companyName);
    if (!row) continue;
    const openCell = row.cells.find(c => c.state === "open");
    if (openCell) {
      return { crewName: row.crewName, dateIso: openCell.dateIso, fitScore: r.score };
    }
  }
  return null;
}
